import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import * as signalrActions from "ngrx-signalr-core";
import { findHub, hubNotFound, mergeMapHubToAction, ofHub } from "ngrx-signalr-core";
import * as pako from 'pako';
import { EMPTY, Observable, catchError, map, merge, mergeMap, of, withLatestFrom } from "rxjs";
import { OPERATOR_ROLES } from "src/app/models/WebServer/partecipantRoles";
import { DisclaimerHelper } from "src/app/helpers/disclaimer.helper";
import { Command } from "src/app/models/WebServer/command";
import { CommandEvent } from "src/app/models/commandEvent";
import { StatusChangeEvent } from "src/app/models/statusChangeEvent";
import { AppConfigService } from "src/app/services/app.config.service";
import { FELoggerService, LogSource } from "src/app/services/feLogger.service";
import { I18Service } from "src/app/services/i18.service";
import { NotificationService } from "src/app/services/notification.service";
import { SessionStatusEvent, ZippedSessionStatusEvent } from "../../models/WebServer/sessionStatusEvent";
import * as appActions from "../actions/app.actions";
import * as sessionActions from "../actions/session.actions";
import * as sessionStatusActions from "../actions/sessionStatus.actions";
import { selectAppState } from "../selectors/app.selectors";
import { selectSession } from "../selectors/session.selectors";
import { selectPartecipantsStatus } from "../selectors/sessionStatus.selectors";
import { AppState } from "../states/app.state";

enum SessionManagerHubEvents {
    PartecipantAdded = 'PartecipantAdded',
    PartecipantRemoved = 'PartecipantRemoved',
    PartecipantStatus = 'PartecipantStatus',
    CommandEvent = 'CommandEvent',
    ExpirationUpdated = 'ExpirationUpdated'
}

enum SessionManagerHubCommands {
    SubscribeToSession = 'SubscribeToSession',
    UpdatePartecipantStatus = 'UpdatePartecipantStatus',
    UpdateSessionStatus = 'UpdateSessionStatus',
    DistpatchCommand = 'DistpatchCommand'
}

const SEND_DELAY = 200;
@Injectable()
export class SignalRHubEffects {
    private timeoutToSend: number = 0;
    private lastUpdateSent: number = Date.now()
    private updateToSend: { partecipantId: string, eventType: StatusChangeEvent, content: any, targetPartecipants: string[] }[] = []

    // Coda messaggi durante riconnessione SignalR
    private messageQueue: Array<{
        cmd: SessionManagerHubCommands,
        data: any,
        reconnect?: boolean
    }> = [];
    private isReconnecting = false;

    constructor(
        private config: AppConfigService,
        private readonly actions$: Actions,
        private readonly store: Store<AppState>
    ) {
        console.debug(`SignalRHubEffects constructor`);
    }

    initSignalR$ = createEffect(() => this.actions$.pipe(
        ofType(signalrActions.signalrHubUnstarted),
        mergeMap((hubConfig) => of(hubConfig).pipe(
            ofHub(hubConfig),
            map((hub) => signalrActions.startSignalRHub(hub))
        ))
        // map(([action, hub]) => startSignalRHub(hub))
    ));

    signalrError$ = createEffect(() => this.actions$.pipe(
        ofType(signalrActions.signalrError),
        map( ({error}) => {
            console.error(`signalrActions.signalrError`,error);
            this.notifyError(error);
        })
    ),{ dispatch: false });

    signalrConnected$ = createEffect(() => this.actions$.pipe(
        ofType(signalrActions.signalrConnected),
        // mergeMap((x) => of(x).pipe(
        //     ofHub(x),
        mergeMapHubToAction(({ action, hub }) => {
            // [PERF-LOAD-10] SignalR Connected
            const perfNow = performance.now();
            const elapsed = perfNow - ((window as any).perfMarks?.browserStart || 0);
            console.log(`[PERF-LOAD-10] SignalR Connected - hubId: ${hub.connectionId} | Time: ${perfNow.toFixed(2)}ms | Elapsed: ${elapsed.toFixed(2)}ms`);

            this.store.dispatch(appActions.loadingMessage({message: ''}))

            this.store.dispatch(appActions.signalrHubConnected({ connectionId: hub.connectionId || '' }));

            console.log(`signalr: signalrConnected hubid:${hub.connectionId}`, action);

            const subscribeToSessionOnStart$ = of(sessionActions.subscribeToSession({reconnect: false}));

            const whenExpirationUpdated$ = hub
                .on<Date>(SessionManagerHubEvents.ExpirationUpdated)
                .pipe(
                    map((newExpiration) => sessionActions.expirationUpdated({ newExpiration }))
                );

            const whenPartecipantAdded$ = hub
                .on<ZippedSessionStatusEvent>(SessionManagerHubEvents.PartecipantAdded)
                .pipe(
                    map(({ data }) => {
                        const {partecipant, session} = this.unzipSessionEvent(data);
                        const myPartecipantId = this.config.partecipantId;
                        if (partecipant.partecipantId === myPartecipantId) {
                            // [PERF-LOAD-12] Participant Added (ME)
                            const perfNow = performance.now();
                            const elapsed = perfNow - ((window as any).perfMarks?.browserStart || 0);
                            console.log(`[PERF-LOAD-12] Participant Added (ME) - hubId: ${hub.connectionId} | Time: ${perfNow.toFixed(2)}ms | Elapsed: ${elapsed.toFixed(2)}ms`);

                            console.log(`signalr: SessionManagerHubEvents.PartecipantAdded MY ${myPartecipantId} hubid:${hub.connectionId}`);
                            this.store.dispatch(appActions.wssSipInit());

                            // Flush della coda messaggi dopo conferma ingresso in sessione
                            // Questo è il momento giusto: il server ha confermato che siamo nella sessione
                            if (this.isReconnecting) {
                                console.log(`PartecipantAdded confirmed, flushing message queue (${this.messageQueue.length} messages)`);
                                this.flushQueue();
                            }
                        }
                        return sessionStatusActions.partecipantAdded({ partecipantStatus: partecipant, sessionStatus: session })
                    })
                );

            const whenPartecipantRemoved$ = hub
                .on<ZippedSessionStatusEvent>(SessionManagerHubEvents.PartecipantRemoved)
                .pipe(
                    map(({ data }) => {
                        const {partecipant, session} = this.unzipSessionEvent(data);
                        return sessionStatusActions.partecipantRemoved({ partecipantStatus: partecipant, sessionStatus: session })
                    })
                );

            const whenPartecipantStatusChange$ = hub
                .on<ZippedSessionStatusEvent>(SessionManagerHubEvents.PartecipantStatus)
                .pipe(
                    withLatestFrom(this.store.select(selectPartecipantsStatus)),
                    mergeMap(([{ data }, currentPartecipants]) => {
                        const {partecipant, session} = this.unzipSessionEvent(data);
                        const actions: any[] = [
                            sessionStatusActions.partecipantStatusChange({ partecipantStatus: partecipant, sessionStatus: session })
                        ];

                        // Rileva transizione isRegistered: false/undefined → true per operatori.
                        // A questo punto lo store ha ancora il VECCHIO stato (il reducer non ha ancora girato),
                        // quindi currentPartecipants contiene il valore precedente di isRegistered.
                        if (OPERATOR_ROLES.includes(partecipant.partecipantRole) && partecipant.isRegistered) {
                            const prevStatus = currentPartecipants.find(p => p.partecipantId === partecipant.partecipantId);
                            if (!prevStatus?.isRegistered) {
                                console.log(`signalr: operator ${partecipant.partecipantId} SIP registered (was: ${prevStatus?.isRegistered})`);
                                actions.push(sessionStatusActions.operatorSipRegistered({ partecipantStatus: partecipant }));
                            }
                        }

                        return actions;
                    })
                );
            const whenCommandEvent$ = hub
                .on<Command>(SessionManagerHubEvents.CommandEvent)
                .pipe(
                    map(({ eventType, content }) => {
                        console.log(`signalr: whenCommandEvent => ${eventType} - ${JSON.stringify(content)}`)
                        return sessionStatusActions.cmd_event({ evt: eventType, val: content })
                    })
                );
            return merge(subscribeToSessionOnStart$, whenPartecipantAdded$, 
                whenPartecipantRemoved$, whenPartecipantStatusChange$, 
                whenCommandEvent$, whenExpirationUpdated$);
        })
        // ))
    ));

    signalrReconnecting$ = createEffect(() => this.actions$.pipe(
        ofType(signalrActions.signalrReconnecting),
        map( (_) => {
            console.log(`signalr: signalrReconnecting - activating message queue`);

            // Marca come in riconnessione - i messaggi verranno accodati
            this.isReconnecting = true;

            // NON azzerare connectionId! Usa valore speciale 'reconnecting'
            // Questo mantiene !!connectionId = true quindi appReady non cade (nessun flickering DOM)
            // Azzerarlo causava appReady=false quando esci da suspended mode prima del reconnect
            return appActions.signalrHubConnected({ connectionId: 'reconnecting' });
        })
    ));
    
    signalrReConnected$ = createEffect(() => this.actions$.pipe(
        ofType(signalrActions.signalrReconnected),
        mergeMapHubToAction(({ action, hub }) => {
            console.log(`signalr: signalrReconnected hubid:${hub.connectionId}`, hub);

            this.store.dispatch(appActions.loadingMessage({message: ''}))

            // IMPORTANTE: Dispatch signalrHubConnected per aggiornare connectionId nello store
            // Questo è CRITICO per appReady = sessionLoaded && wssSipRegistered && connectionId
            this.store.dispatch(appActions.signalrHubConnected({ connectionId: hub.connectionId || '' }));
            console.log(`***** Dispatched signalrHubConnected with connectionId: ${hub.connectionId}`);

            this.store.dispatch(sessionActions.subscribeToSession({reconnect: true}));

            // Ricarica session parameters dopo riconnessione
            // Necessario perché durante la disconnessione l'operatore potrebbe aver aggiunto/rimosso guest
            this.store.dispatch(sessionActions.sessionReloadFromServer());

            // Ritorna EMPTY perché tutti i dispatch sono già stati fatti manualmente
            return EMPTY;
        })
    ),{ dispatch: false });

    signalrDisconnected$ = createEffect(() => this.actions$.pipe(
        ofType(signalrActions.signalrDisconnected),
        withLatestFrom(this.store.select(selectAppState)),
        map(([x, state]) => {
            console.log(`signalr: signalrDisconnected`, x);

            // Se in suspended mode, non mostrare errore e lascia riconnessione automatica
            if (state.isSuspended) {
                console.log('SignalR disconnected during suspended mode - silent reconnection will occur');
                return appActions.loadingMessage({message: ''}); // Non mostrare errore
            }

            // Comportamento normale
            NotificationService.showError(I18Service.get('SNACKBAR.DISCONNECTED'));
            return appActions.showErrorPage({message: I18Service.get('SNACKBAR.DISCONNECTED')})
        })
    ));

    subscribeToSession$ = createEffect(() => this.actions$.pipe(
        ofType(sessionActions.subscribeToSession),
        mergeMap((action) => {
            // [PERF-LOAD-11] SignalR Subscribe Start
            const perfNow = performance.now();
            const elapsed = perfNow - ((window as any).perfMarks?.browserStart || 0);
            console.log(`[PERF-LOAD-11] SignalR Subscribe Start | Time: ${perfNow.toFixed(2)}ms | Elapsed: ${elapsed.toFixed(2)}ms`);

            const partecipantId = this.config.partecipantId;

            // Se è un reconnect, ripristina stato critico da localStorage PRIMA di inviare
            // Questo previene blocchi UI se il backend ha perso lo stato in cache
            if (action.reconnect) {
                this.restoreCriticalStateFromLocalStorage();
            }

            // NON fare flush qui! Aspetta l'evento PartecipantAdded che conferma l'ingresso in sessione
            return this.send(SessionManagerHubCommands.SubscribeToSession, partecipantId, action.reconnect);
        }),
        catchError((error: any) => {
            // console.error(`subscribeToSession error: `, error);
            FELoggerService.warn(`subscribeToSession error ${error}`);
            const s = error.toString().split('- error:');
            if (s.length > 1) {
                error = I18Service.get(`Error.${s[1]}`);
            } else {
                if (error.toString().includes('Session Expired')) {
                    error = I18Service.get(`POPUP.SESSIONEXPIREDBODY`);
                }
            }

            this.store.dispatch(sessionActions.sessionFailure({ error, showReload: false }));
            return EMPTY;
        })
    ), { dispatch: false });

    evt_changeStatus$ = createEffect(() => this.actions$.pipe(
        ofType(sessionStatusActions.evt_changeStatus),
        mergeMap(({ evt, val }) => {
            const partecipantId = this.config.partecipantId
            if (evt == StatusChangeEvent.MultipleUpdateStatus) {
                const statusArray = val.map((z: any) => { return { partecipantId, eventType: z.evt, content: z.val, targetPartecipants: [] } })
                const evts= val.map((z: any) => { return { evt: z.evt, val: z.val} })
                console.log(`signalr: evt_changeStatus MultipleUpdateStatus => ${JSON.stringify(evts)}`)
                this.lastUpdateSent = Date.now();
                return this.send(SessionManagerHubCommands.UpdatePartecipantStatus, { partecipantId, eventType: evt, content: statusArray, targetPartecipants: [] });
            }
            console.log(`signalr: evt_changeStatus MultipleUpdateStatus => ${JSON.stringify({evt, val})}`)
            return this.send(SessionManagerHubCommands.UpdatePartecipantStatus, { partecipantId, eventType: evt, content: val, targetPartecipants: [] });


            // if (Date.now() - SEND_DELAY < this.lastUpdateSent) {
            //     if(this.timeoutToSend) {clearTimeout(this.timeoutToSend); this.timeoutToSend = 0;}
            //     this.updateToSend.push({ partecipantId, eventType: evt, content: val, targetPartecipants: [] });
            //     this.timeoutToSend  = window.setTimeout(()=>{
            //         .... // creare un multiple ed inviare nuovo "evento"
            //     }, SEND_DELAY * 1.2);
            //     return EMPTY;
            // }
            // if(this.timeoutToSend) {clearTimeout(this.timeoutToSend); this.timeoutToSend = 0;}
            // this.lastUpdateSent = Date.now();
            // this.updateToSend.push({ partecipantId, eventType: evt, content: val, targetPartecipants: [] });
            // let newData: { partecipantId: string, eventType: StatusChangeEvent, content: any, targetPartecipants: string[] } | undefined;
            // if (this.updateToSend.length > 1) {
            //     const multiStatus: any = [];
            //     this.updateToSend.forEach( s => multiStatus.push(s));
            //     newData = {partecipantId, eventType: StatusChangeEvent.MultipleUpdateStatus, content: multiStatus, targetPartecipants:[]}
            // }
            // else {
            //     newData = this.updateToSend.pop();
            // }
            
            // this.updateToSend = [];
            // if (!newData){
            //     console.error(`***** STRANGE no change status to send ****`)
            //     return EMPTY;
            // }
            // console.log(`sending `)
            // return this.send(SessionManagerHubCommands.UpdatePartecipantStatus, newData);



        }),
        catchError((error: any) => {
            this.notifyError(error);
            return EMPTY;
        })
    ), { dispatch: false });

    cmd_dispatch$ = createEffect(() => this.actions$.pipe(
        ofType(sessionStatusActions.cmd_dispatch),
        mergeMap(({ cmd, val, toIds }) => {
            const partecipantId = this.config.partecipantId;

            const command: Command = {
                partecipantId, eventType: cmd, content: val, targetPartecipants: toIds
            }

            return this.send(SessionManagerHubCommands.DistpatchCommand, command);
        }),
        catchError((error: any) => {
            this.notifyError(error);
            return EMPTY;
        })
    ), { dispatch: false });

    terminateSession$ = createEffect(() => this.actions$.pipe(
        ofType(sessionActions.terminateSession),
        withLatestFrom(this.store.select(selectSession)),
        mergeMap(([_, session]) => {
            const partecipantId = this.config.partecipantId;
            const expiration=new Date(session.expiration).getTime();
            const now = new Date().getTime();
            if (!this.config.isReceiver || expiration + 30 * 1000 < now ) {
                // solo operatore manda il termina sessione.. gli altri.. escono e boh
                return EMPTY;
            }

            const command: Command = {
                partecipantId, eventType: CommandEvent.Session_Terminate, content: '', targetPartecipants: ['*']
            }
            return this.send(SessionManagerHubCommands.DistpatchCommand, command);
        }),
        catchError((error: any) => {
            this.notifyError(error);
            return EMPTY;
        })
    ), { dispatch: false });

    extendSession$ = createEffect(() => this.actions$.pipe(
        ofType(sessionActions.extendSession),
        mergeMap( action => {
            const partecipantId = this.config.partecipantId;
            console.log(`signalr: sending Session_Extend => ${partecipantId} minutes ${action.minutes}`);

            const command: Command = {
                partecipantId, eventType: CommandEvent.Session_Extend, content: action.minutes, targetPartecipants: ['*']
            }
            return this.send(SessionManagerHubCommands.DistpatchCommand, command);
        }),
        catchError((error: any) => {
            this.notifyError(error);
            return EMPTY;
        })
    ), { dispatch: false });


    private send(cmd: SessionManagerHubCommands, data: any, reconnect: boolean | undefined= undefined): Observable<any> {
        // Se in riconnessione E NON è subscribeToSession, accoda il messaggio
        // SubscribeToSession deve sempre passare perché è il comando che completa la riconnessione
        if (this.isReconnecting && cmd !== SessionManagerHubCommands.SubscribeToSession) {
            const cmdName = data.eventType || cmd;
            console.warn(`SignalR reconnecting, queueing command: ${cmdName}`);
            this.messageQueue.push({ cmd, data, reconnect });
            return EMPTY; // Non inviare ora, sarà reinviato dopo subscribeToSession
        }

        const hubConfig = this.config.signalRHub;
        const hub = findHub(hubConfig);
        if (!hub || !hub.connectionId) {
            return of(hubNotFound(hubConfig));
        }
        const strcmd = data.eventType || cmd
        const strval = JSON.stringify( data.content );
        console.log(`signalr: sending ${strcmd} => ${strval} - hubid:${hub.connectionId}`)

        let sendHub: Observable<any>;
        if (reconnect !== undefined) {
            sendHub = hub.send(cmd, data, reconnect);
        } else {
            sendHub = hub.send(cmd, data);
        }

        return sendHub.pipe(
            map( _ =>{}),
            catchError((error: any) => {
                this.notifyError(error);
                return EMPTY;
            })
        );
    }

    /**
     * Svuota la coda di messaggi accodati durante la riconnessione.
     * Chiamato dopo che subscribeToSession completa con successo.
     */
    private flushQueue(): void {
        if (this.messageQueue.length === 0) {
            console.log('SignalR queue empty, nothing to flush');
            this.isReconnecting = false;
            return;
        }

        console.log(`SignalR flushing ${this.messageQueue.length} queued messages`);

        const queue = [...this.messageQueue]; // Copia per sicurezza
        this.messageQueue = []; // Svuota subito per evitare loop
        this.isReconnecting = false; // Fine riconnessione

        // Reinvia tutti i messaggi accodati
        queue.forEach(({ cmd, data, reconnect }) => {
            const cmdName = data.eventType || cmd;
            console.log(`Re-sending queued command: ${cmdName}`);

            // Ri-invoca send() che ora non accoderà più (isReconnecting=false)
            this.send(cmd, data, reconnect).subscribe({
                error: (err) => {
                    console.error(`Failed to resend queued command ${cmdName}:`, err);
                    // Messaggio perso - potremmo notificare l'utente o riaccordare
                    FELoggerService.error(`Failed to resend queued command ${cmdName}`, LogSource.Default, err);
                }
            });
        });
    }

    private notifyError(error: any) {
        if ( (''+error).includes('Invocation canceled due to the underlying connection being closed')) return;
        if ( (''+error).includes('WebSocket closed with status code')) return;
        console.trace(`signalr error: ${error}`, error)

        FELoggerService.warn(`subscribeToSession error ${error}`);
        let showReload = true;
        let errMsg = I18Service.get('Error.Signalr');
        const s = error.toString().split('- error:');
        if (s.length > 1) {
            errMsg = I18Service.get(`Error.${s[1]}`);
            showReload = false;
        } else {
            if (error.toString().includes('Session Expired')) {
                errMsg = I18Service.get(`POPUP.SESSIONEXPIREDBODY`);
                showReload = false;
            }
        }
        if (errMsg) {
            this.store.dispatch(sessionActions.sessionFailure({ error: errMsg, showReload }));
        }
        else        
        NotificationService.showError(I18Service.get('Error.Signalr'));
    }

    private unzipSessionEvent(data: string): SessionStatusEvent {
        const strData     = window.atob(data);

        // Convert binary string to character-number array
        const charData    = strData.split('').map( z => z.charCodeAt(0) );

        // Turn number array into byte-array
        const binData     = new Uint8Array(charData);

        // Pako magic
        const dataInflated = pako.inflate(binData);

        const restored =  new TextDecoder().decode(dataInflated);
        const x = JSON.parse(restored);
        console.log(`unzipSessionEvent`,x);
        return x;
    }

    /**
     * Ripristina lo stato critico da localStorage dopo un reconnect.
     * Usato per prevenire blocchi UI quando il backend perde lo stato in cache.
     *
     * Replica la logica di DisclaimerHelper.showDisclaimer() ma in modo silenzioso:
     * - Se disclaimer non richiesto dall'agency → accettato
     * - Se già accettato in localStorage (con timeout) → ripristina
     * - Altrimenti non fa nulla (disclaimer deve essere mostrato normalmente)
     */
    private restoreCriticalStateFromLocalStorage() {
        try {
            // Controlla se disclaimer dovrebbe essere considerato accettato
            const shouldBeAccepted = DisclaimerHelper.shouldDisclaimerBeAccepted();

            if (shouldBeAccepted) {
                console.log('Restoring disclaimerAccepted=true from localStorage after reconnect');

                // Dispatch IMMEDIATO per prevenire blocchi UI prima che arrivi lo stato dal backend
                this.store.dispatch(sessionStatusActions.evt_changeStatus({
                    evt: StatusChangeEvent.DisclaimerAccepted,
                    val: true
                }));
            } else {
                console.log('Disclaimer not found in localStorage - user will need to accept it again');
            }

            // 🎯 FUTURE: Qui possiamo aggiungere ripristino di altri campi critici
            // es. permissions, device settings, ecc.

        } catch (error) {
            FELoggerService.warn('Error restoring critical state from localStorage', undefined, error);
        }
    }
}